import { Fragment, memo, useEffect, useMemo, useState } from "react";
import { supabase } from "../../../../../integrations/supabase/client";
import { Card } from "../../../../ui/card";
import { Skeleton } from "../../../../ui/skeleton";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import {
  ImageIcon, MessageSquareOff, BadgeCheck, Reply, Star, Users, MessageSquare, Repeat,
  Clock, Coins, Sparkles, AlertTriangle, CheckCircle2, Inbox,
} from "lucide-react";
import { Badge } from "../../../../ui/badge";
import { ToggleGroup, ToggleGroupItem } from "../../../../ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../ui/dialog";
import { ScrollArea } from "../../../../ui/scroll-area";
import {
  EXPERIENCE_CATEGORIES,
  classifyReviewsByCategory,
  extractKeywordSentiment,
  bucketReviewsByDay,
  type ReviewLite,
  type CategoryStats,
  type KeywordStat,
  type DayBucket,
} from "../../../../../utils/reviewClassification";

type PeriodDays = 7 | 30 | 90;

interface AnalyticsTabProps {
  selectedOutlets: string[];
  supabaseoutletIDs: string[];
}

interface ReviewRow {
  id: string;
  outlet_id: string | null;
  platform: string | null;
  rating: number | null;
  comment_text: string | null;
  photos: string[] | null;
  created_at: string | null;
  posted_at: string | null;
  author_name: string | null;
}

interface MonthPoint {
  month: string;
  label: string;
  total: number;
  avg: number | null;
}

const isAll = (sel: string[]) => sel.length === 0 || sel.includes("all");

export const AnalyticsTab = ({ selectedOutlets, supabaseoutletIDs }: AnalyticsTabProps) => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [distinctRepliedReviewIds, setDistinctRepliedReviewIds] = useState(0);
  const [totalRepliesRows, setTotalRepliesRows] = useState(0);
  const [outletsMap, setOutletsMap] = useState<Record<string, string>>({});
  const [periodDays, setPeriodDays] = useState<PeriodDays>(30);

  // Period-scoped reviews used only by the three new sections (A/B/C).
  // Existing sections continue to use the full dataset to preserve behaviour.
  const periodReviews = useMemo<ReviewRow[]>(() => {
    if (!reviews.length) return [];
    const cutoff = Date.now() - periodDays * 86400000;
    return reviews.filter((r) => {
      const raw = r.posted_at || r.created_at;
      if (!raw) return false;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) && t >= cutoff;
    });
  }, [reviews, periodDays]);

  const outletIdsInScope = useMemo(() => {
    const s = new Set<string>();
    for (const r of periodReviews) if (r.outlet_id) s.add(r.outlet_id);
    return Array.from(s);
  }, [periodReviews]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Fetch reviews (exclude epicreviews per project rule)
        let q = supabase
          .from("reviews")
          .select("id, outlet_id, platform, rating, comment_text, photos, created_at, posted_at, author_name")
          .neq("platform", "epicreviews")
          .order("created_at", { ascending: true })
          .limit(10000);

        if (!isAll(selectedOutlets)) {
          q = q.in("outlet_id", selectedOutlets);
        }else {
          q = q.in("outlet_id", supabaseoutletIDs);
        }
        const { data: rev, error } = await q;
        if (error) throw error;

        const reviewRows = (rev as ReviewRow[]) || [];
        const reviewIds = reviewRows.map((r) => r.id);

        // Fetch replies — count distinct review_id values matching the reviews in scope.
        // Previously this filtered review_replies.outlet_id which is often null/unset.
        let distinctReplied = 0;
        let repliesTotal = 0;
        let rawReplies: any = null;
        if (reviewIds.length > 0) {
          const { data: replies, error: rErr, count } = await supabase
            .from("review_replies")
            .select("review_id", { count: "exact" })
            .in("review_id", reviewIds);
          rawReplies = { data: replies, error: rErr, count };
          if (!rErr && replies) {
            const set = new Set<string>();
            for (const row of replies as Array<{ review_id: string | null }>) {
              if (row.review_id) set.add(row.review_id);
            }
            distinctReplied = set.size;
            repliesTotal = count ?? replies.length;
          }
        }

        // ---- Local Guides probe ----
        // The reviews schema does NOT contain is_local_guide / reviewer_type / contributor_type.
        // Probe author_name only as a fallback identity proxy and log raw response.
        const { data: lgProbe, error: lgErr } = await supabase
          .from("reviews")
          .select("id, author_name")
          .limit(1);
        console.log("[AnalyticsTab] Local Guides probe — raw Supabase response:", { data: lgProbe, error: lgErr, note: "is_local_guide / reviewer_type / contributor_type columns do not exist on reviews table — showing 'Not tracked'." });
        console.log("[AnalyticsTab] review_replies count query — raw response:", rawReplies, { distinctRepliedReviewIds: distinctReplied, totalRows: repliesTotal });

        if (!cancelled) {
          setReviews(reviewRows);
          setDistinctRepliedReviewIds(distinctReplied);
          setTotalRepliesRows(repliesTotal);
        }
      } catch (e) {
        console.error("AnalyticsTab load error", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedOutlets]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("outlets").select("id, outlet_name").in("id", supabaseoutletIDs);
      if (!cancelled && data) {
        const m: Record<string, string> = {};
        for (const o of data as Array<{ id: string; outlet_name: string }>) m[o.id] = o.outlet_name;
        setOutletsMap(m);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const timeline = useMemo<MonthPoint[]>(() => {
    if (reviews.length === 0) return [];
    const buckets = new Map<string, { count: number; ratingSum: number; ratingN: number }>();
    for (const r of reviews) {
      const d = r.posted_at || r.created_at;
      if (!d) continue;
      const dt = new Date(d);
      if (isNaN(dt.getTime())) continue;
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const b = buckets.get(key) || { count: 0, ratingSum: 0, ratingN: 0 };
      b.count += 1;
      const ratingNum = typeof r.rating === "number" ? r.rating : Number(r.rating);
      if (Number.isFinite(ratingNum) && ratingNum > 0) {
        b.ratingSum += ratingNum;
        b.ratingN += 1;
      }
      buckets.set(key, b);
    }
    const sorted = Array.from(buckets.entries()).sort(([a], [b]) => a.localeCompare(b));
    let cumulative = 0;
    const result = sorted.map(([key, b]) => {
      cumulative += b.count;
      const [y, m] = key.split("-");
      const labelDate = new Date(Number(y), Number(m) - 1, 1);
      // Monthly avg = sum of ratings / count of rated reviews in that month.
      // Use null (not 0) when no rated reviews exist so the dashed line skips/connects across gaps.
      const avg = b.ratingN > 0 ? Number((b.ratingSum / b.ratingN).toFixed(2)) : null;
      return {
        month: key,
        label: labelDate.toLocaleString(undefined, { month: "short", year: "2-digit" }),
        total: cumulative,
        avg,
      };
    });
    console.log("[AnalyticsTab] Timeline first 5 data points:", result.slice(0, 5));
    console.log("[AnalyticsTab] Avg ratings across all months:", result.map((p) => ({ month: p.month, count: buckets.get(p.month)?.count, ratedN: buckets.get(p.month)?.ratingN, avg: p.avg })));
    return result;
  }, [reviews]);

  const summary = useMemo(() => {
    const src = periodReviews;
    const total = src.length;

    const rated = src.filter((r) => Number.isFinite(r.rating as number) && (r.rating as number) > 0);
    const avgReviewRating = rated.length > 0
      ? Number((rated.reduce((s, r) => s + (r.rating as number), 0) / rated.length).toFixed(2))
      : null;

    const platformCounts = src.reduce<Record<string, number>>((acc, r) => {
      const key = r.platform == null ? "(null)" : String(r.platform);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    console.log("[AnalyticsTab] Distinct platform values in selected scope:", platformCounts);

    const hasAnyPlatform = src.some((r) => r.platform != null && String(r.platform).trim() !== "");
    const googleRated = rated.filter((r) => /google/i.test(String(r.platform || "")));
    let googleRating: number | null = null;
    let googleFallback = false;
    let googleSampleSize = googleRated.length;
    if (googleRated.length > 0) {
      googleRating = Number((googleRated.reduce((s, r) => s + (r.rating as number), 0) / googleRated.length).toFixed(2));
    } else if (!hasAnyPlatform && rated.length > 0) {
      googleRating = avgReviewRating;
      googleFallback = true;
      googleSampleSize = rated.length;
    }

    const reviewers = new Set<string>();
    for (const r of src) {
      const name = (r.author_name || "").trim();
      if (name) reviewers.add(name.toLowerCase());
    }
    const avgPerReviewer = reviewers.size > 0
      ? Number((total / reviewers.size).toFixed(2))
      : null;

    return {
      total,
      avgReviewRating,
      googleRating,
      googleFallback,
      avgPerReviewer,
      reviewerCount: reviewers.size,
      googleSampleSize,
    };
  }, [periodReviews]);

  const velocity = useMemo(() => {
    // Use exact UTC midnight cutoffs from today
    const now = new Date();
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const day = 86400000;
    const cutoff30 = todayUTC - 30 * day;
    const cutoff180 = todayUTC - 180 * day;
    const cutoff365 = todayUTC - 365 * day;

    const tsOf = (r: ReviewRow) => {
      const d = r.posted_at || r.created_at;
      if (!d) return 0;
      const t = new Date(d).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    const within = (cutoff: number) => reviews.filter((r) => tsOf(r) >= cutoff).length;

    const last30 = within(cutoff30);
    const last180 = within(cutoff180);
    const last365 = within(cutoff365);
    return {
      last30: { count: last30, perMonth: last30 },
      last180: { count: last180, perMonth: Math.round((last180 / 6) * 10) / 10 },
      last365: { count: last365, perMonth: Math.round((last365 / 12) * 10) / 10 },
    };
  }, [reviews]);

  const quality = useMemo(() => {
    const src = periodReviews;
    const total = src.length || 1;
    const withPhotos = src.filter((r) => Array.isArray(r.photos) && r.photos.length > 0).length;
    const noText = src.filter((r) => !r.comment_text || r.comment_text.trim().length === 0).length;
    const responseRate = src.length > 0
      ? Math.min(100, Math.round((distinctRepliedReviewIds / src.length) * 1000) / 10)
      : 0;
    const pct = (n: number) => Math.round((n / total) * 100);
    return {
      withPhotos: { count: withPhotos, pct: pct(withPhotos) },
      noText: { count: noText, pct: pct(noText) },
      responseRate,
    };
  }, [periodReviews, distinctRepliedReviewIds]);

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-[360px] w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <Card className="p-12 text-center">
        <h3 className="text-lg font-semibold mb-1">No review data yet</h3>
        <p className="text-sm text-muted-foreground">
          Once reviews are synced, analytics will appear here.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-10">
      {/* Period selector — drives Sections A/B/C */}
      <div className="flex items-center justify-end">
        <ToggleGroup
          type="single"
          value={String(periodDays)}
          onValueChange={(v) => v && setPeriodDays(Number(v) as PeriodDays)}
          className="bg-muted rounded-lg p-0.5"
        >
          <ToggleGroupItem value="7" className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md">7d</ToggleGroupItem>
          <ToggleGroupItem value="30" className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md">30d</ToggleGroupItem>
          <ToggleGroupItem value="90" className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md">90d</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* SECTION 0 — Summary Stats */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-bold">Summary</h2>
          <p className="text-sm text-muted-foreground">Headline metrics across the selected outlet(s).</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryStat
            icon={<MessageSquare className="h-5 w-5" />}
            label="Total Reviews"
            value={summary.total.toLocaleString()}
            sub="All platforms (excl. EpicReviews)"
          />
          <SummaryStat
            icon={<Star className="h-5 w-5" />}
            label={summary.googleFallback ? "Avg Rating" : "Google Rating"}
            value={summary.googleRating !== null ? summary.googleRating.toFixed(2) : "—"}
            sub={
              summary.googleFallback
                ? `Fallback: avg of ${summary.googleSampleSize} reviews (no platform field)`
                : summary.googleSampleSize > 0
                  ? `Avg of ${summary.googleSampleSize} Google reviews`
                  : "No Google reviews yet"
            }
          />
          <SummaryStat
            icon={<Star className="h-5 w-5" />}
            label="Avg Review Rating"
            value={summary.avgReviewRating !== null ? summary.avgReviewRating.toFixed(2) : "—"}
            sub="Across all platforms"
          />
          <SummaryStat
            icon={<Repeat className="h-5 w-5" />}
            label="Avg Reviews / Reviewer"
            value={summary.avgPerReviewer !== null ? summary.avgPerReviewer.toFixed(2) : "—"}
            sub={summary.reviewerCount > 0 ? `${summary.reviewerCount.toLocaleString()} unique reviewers` : "No named reviewers"}
          />
        </div>
      </section>

      {/* SECTION 1 — Review Timeline */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-bold">Review Timeline</h2>
          <p className="text-sm text-muted-foreground">
            Cumulative review volume and monthly average rating.
          </p>
        </div>
        <Card className="p-4">
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={timeline} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis
                  yAxisId="left"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  allowDecimals={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, 5]}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    color: "hsl(var(--popover-foreground))",
                  }}
                  formatter={(value: any, name: string) => {
                    if (name === "Avg Rating") return [value ?? "—", name];
                    return [value, name];
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  content={() => (
                    <ul className="flex items-center justify-center gap-6 pt-3 text-xs text-muted-foreground list-none m-0 p-0">
                      <li className="flex items-center gap-2">
                        <svg width="32" height="10" viewBox="0 0 32 10">
                          <line x1="0" y1="5" x2="32" y2="5" stroke="hsl(var(--primary))" strokeWidth="2" />
                          <circle cx="16" cy="5" r="2.75" fill="hsl(var(--primary))" />
                        </svg>
                        <span>Total Reviews</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg width="32" height="10" viewBox="0 0 32 10">
                          <line x1="0" y1="5" x2="32" y2="5" stroke="hsl(var(--chart-2, 24 95% 53%))" strokeWidth="2" strokeDasharray="5 3" />
                        </svg>
                        <span>Avg Rating</span>
                      </li>
                    </ul>
                  )}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="total"
                  name="Total Reviews"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "hsl(var(--primary))" }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="avg"
                  name="Avg Rating"
                  stroke="hsl(var(--chart-2, 24 95% 53%))"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </section>

      {/* SECTION 2 — Velocity */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-bold">Review Velocity</h2>
          <p className="text-sm text-muted-foreground">How often new reviews arrive over time.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <VelocityCard label="Last 30 days" count={velocity.last30.count} perMonth={velocity.last30.perMonth} />
          <VelocityCard label="Last 6 months" count={velocity.last180.count} perMonth={velocity.last180.perMonth} />
          <VelocityCard label="Last 1 year" count={velocity.last365.count} perMonth={velocity.last365.perMonth} />
        </div>
      </section>

      {/* SECTION 3 — Quality Signals */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-bold">Review Quality Signals</h2>
          <p className="text-sm text-muted-foreground">
            Signals about review depth, media, and your response activity.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <QualityTile
            icon={<ImageIcon className="h-5 w-5" />}
            metric={`${quality.withPhotos.count}`}
            sub={`${quality.withPhotos.pct}% of total`}
            label="Reviews with photos"
          />
          <QualityTile
            icon={<MessageSquareOff className="h-5 w-5" />}
            metric={`${quality.noText.count}`}
            sub={`${quality.noText.pct}% of total`}
            label="Reviews without text"
          />
          <QualityTile
            icon={<BadgeCheck className="h-5 w-5" />}
            metric={<Badge variant="secondary" className="text-xs font-medium">Not tracked</Badge>}
            sub="Field unavailable in source data"
            label="Reviews from Local Guides"
          />
          <QualityTile
            icon={<Reply className="h-5 w-5" />}
            metric={`${quality.responseRate.toFixed(1)}%`}
            sub={`${distinctRepliedReviewIds.toLocaleString()} of ${reviews.length.toLocaleString()} reviews replied${totalRepliesRows !== distinctRepliedReviewIds ? ` · ${totalRepliesRows} reply rows total` : ""}`}
            label="Response rate"
          />
        </div>
      </section>

      {/* SECTION A — Experience Categories */}
      <ExperienceCategoriesSection
        reviews={periodReviews}
        outletIds={outletIdsInScope}
        outletsMap={outletsMap}
      />

      {/* SECTION B — Keyword Sentiment Split */}
      <KeywordSentimentSection reviews={periodReviews} />

      {/* SECTION C — Sentiment by Day Heatmap */}
      <SentimentHeatmapSection reviews={periodReviews} />
    </div>
  );
};

const VelocityCard = ({
  label,
  count,
  perMonth,
}: {
  label: string;
  count: number;
  perMonth: number;
}) => (
  <Card className="p-5">
    <div className="text-[28px] leading-tight font-bold">{count.toLocaleString()}</div>
    <div className="text-sm text-muted-foreground mt-1">{label}</div>
    <div className="text-xs text-muted-foreground mt-2">~{perMonth} avg per month</div>
  </Card>
);

const QualityTile = ({
  icon,
  metric,
  sub,
  label,
}: {
  icon: React.ReactNode;
  metric: React.ReactNode;
  sub: string;
  label: string;
}) => (
  <Card className="p-5 flex items-center gap-4">
    <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2 flex-wrap">
        <div className="text-[28px] leading-tight font-bold">{metric}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
      <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
    </div>
  </Card>
);

const SummaryStat = ({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) => (
  <Card className="p-5">
    <div className="flex items-center gap-2 text-muted-foreground">
      <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="text-xs font-medium uppercase tracking-wide">{label}</div>
    </div>
    <div className="text-[28px] leading-tight font-bold mt-3">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </Card>
);

// ============================================================================
// SECTION A — Experience Categories
// ============================================================================
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  staff: <Users className="h-4 w-4" />,
  speed: <Clock className="h-4 w-4" />,
  quality: <Star className="h-4 w-4" />,
  value: <Coins className="h-4 w-4" />,
  cleanliness: <Sparkles className="h-4 w-4" />,
};

const SectionEmpty = ({ message = "No data available for this period" }: { message?: string }) => (
  <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
    <Inbox className="h-8 w-8 mb-2 opacity-40" />
    <p className="text-sm">{message}</p>
  </div>
);

const ExperienceCategoriesSection = memo(({
  reviews,
  outletIds,
  outletsMap,
}: {
  reviews: ReviewRow[];
  outletIds: string[];
  outletsMap: Record<string, string>;
}) => {
  const [scope, setScope] = useState<"brand" | "outlet">("brand");
  const [activeOutlet, setActiveOutlet] = useState<string | null>(null);

  const scoped = useMemo(() => {
    if (scope === "outlet" && activeOutlet) {
      return reviews.filter((r) => r.outlet_id === activeOutlet);
    }
    return reviews;
  }, [reviews, scope, activeOutlet]);

  const stats: CategoryStats[] = useMemo(() => classifyReviewsByCategory(scoped), [scoped]);
  const worst = useMemo(
    () => stats.filter((s) => s.matched > 0).sort((a, b) => b.negativePct - a.negativePct)[0],
    [stats],
  );

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold">Experience categories</h2>
          <p className="text-sm text-muted-foreground">What customers are saying — by theme</p>
        </div>
        <ToggleGroup
          type="single"
          value={scope}
          onValueChange={(v) => v && setScope(v as "brand" | "outlet")}
          className="bg-muted rounded-lg p-0.5"
        >
          <ToggleGroupItem value="brand" className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md">Brand</ToggleGroupItem>
          <ToggleGroupItem value="outlet" className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md">Outlet</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {scope === "outlet" && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-1 px-1">
          {outletIds.length === 0 && (
            <span className="text-xs text-muted-foreground">No outlets in this period</span>
          )}
          {outletIds.map((id) => {
            const isActive = activeOutlet === id;
            return (
              <button
                key={id}
                onClick={() => setActiveOutlet(isActive ? null : id)}
                className={`shrink-0 rounded-full border px-3 py-1 text-xs whitespace-nowrap transition-colors ${
                  isActive ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"
                }`}
              >
                {outletsMap[id] || id.slice(0, 8)}
              </button>
            );
          })}
        </div>
      )}

      {scoped.length === 0 ? (
        <SectionEmpty />
      ) : (
        <>
          <div className="space-y-3">
            {stats.map((s) => (
              <div key={s.key} className="flex items-center gap-4">
                <div className="flex items-center gap-2 w-32 shrink-0 text-sm">
                  <span className="text-muted-foreground">{CATEGORY_ICONS[s.key]}</span>
                  <span className="font-medium">{s.label}</span>
                </div>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "10px",
                    backgroundColor: "#e2e8f0",
                    borderRadius: "9999px",
                    overflow: "hidden",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "100%",
                      width: `${s.positivePct}%`,
                      backgroundColor: "#16a34a",
                      borderRadius: "9999px",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      height: "100%",
                      width: `${s.negativePct}%`,
                      backgroundColor: "#dc2626",
                      borderRadius: "9999px",
                    }}
                  />
                </div>
                <div style={{ minWidth: "80px", textAlign: "right" }} className="shrink-0 tabular-nums">
                  <span style={{ color: "#16a34a", fontSize: "13px", fontWeight: 500 }}>{s.positivePct}% / </span>
                  <span style={{ color: "#dc2626", fontSize: "13px", fontWeight: 500 }}>{s.negativePct}%</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5">
            {worst && worst.negativePct > 35 ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <strong>{worst.label}</strong> is your weakest area — {worst.negativePct}% of mentions are negative.
                  Consider addressing this in your review responses.
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <span>All experience categories are performing well this period.</span>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
});
ExperienceCategoriesSection.displayName = "ExperienceCategoriesSection";

// ============================================================================
// SECTION B — Keyword Sentiment Split
// ============================================================================
const KeywordSentimentSection = memo(({ reviews }: { reviews: ReviewRow[] }) => {
  const [filter, setFilter] = useState<"all" | "positive" | "negative">("all");
  const keywords: KeywordStat[] = useMemo(() => extractKeywordSentiment(reviews, 80), [reviews]);

  const visible = useMemo(() => {
    if (filter === "positive") {
      return keywords
        .filter((k) => k.positive > k.negative)
        .sort((a, b) => b.positive - a.positive)
        .slice(0, 24);
    }
    if (filter === "negative") {
      return keywords
        .filter((k) => k.negative > k.positive)
        .sort((a, b) => b.negative - a.negative)
        .slice(0, 24);
    }
    return [...keywords]
      .sort((a, b) => (b.positive + b.negative) - (a.positive + a.negative))
      .slice(0, 24);
  }, [keywords, filter]);

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold">Keyword sentiment</h2>
          <p className="text-sm text-muted-foreground">Most mentioned words — with positive and negative split</p>
        </div>
        <ToggleGroup
          type="single"
          value={filter}
          onValueChange={(v) => v && setFilter(v as typeof filter)}
          className="bg-muted rounded-lg p-0.5"
        >
          <ToggleGroupItem value="all" className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md">All</ToggleGroupItem>
          <ToggleGroupItem value="positive" className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md">Positive</ToggleGroupItem>
          <ToggleGroupItem value="negative" className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md">Negative</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {visible.length === 0 ? (
        <SectionEmpty />
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", padding: "8px 0" }}>
          {visible.map((k) => (
            <div
              key={k.word}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 14px",
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "9999px",
              }}
            >
              <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{k.word}</span>
              {k.positive > 0 && (
                <span
                  style={{
                    background: "#dcfce7",
                    color: "#15803d",
                    fontSize: "11px",
                    fontWeight: 500,
                    padding: "2px 7px",
                    borderRadius: "9999px",
                  }}
                >
                  +{k.positive}
                </span>
              )}
              {k.negative > 0 && (
                <span
                  style={{
                    background: "#fee2e2",
                    color: "#991b1b",
                    fontSize: "11px",
                    fontWeight: 500,
                    padding: "2px 7px",
                    borderRadius: "9999px",
                  }}
                >
                  -{k.negative}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
});
KeywordSentimentSection.displayName = "KeywordSentimentSection";

// ============================================================================
// SECTION C — Sentiment by Day Heatmap (last 8 weeks)
// ============================================================================
const colorForCell = (b: DayBucket): string => {
  if (b.total === 0) return "#f1f5f9";
  const p = b.positivePct;
  if (p <= 30) return "#fca5a5";
  if (p <= 60) return "#fcd34d";
  if (p <= 80) return "#86efac";
  return "#16a34a";
};

const SentimentHeatmapSection = memo(({ reviews }: { reviews: ReviewRow[] }) => {
  const days = useMemo(() => bucketReviewsByDay(reviews, 56), [reviews]);
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const dayReviews = useMemo(() => {
    if (!selectedDay) return [] as ReviewRow[];
    return reviews
      .filter((r) => {
        const raw = r.posted_at || r.created_at;
        if (!raw) return false;
        const d = new Date(raw);
        if (isNaN(d.getTime())) return false;
        d.setHours(0, 0, 0, 0);
        return d.toISOString().slice(0, 10) === selectedDay;
      })
      .sort((a, b) => {
        const ta = new Date(a.posted_at || a.created_at || 0).getTime();
        const tb = new Date(b.posted_at || b.created_at || 0).getTime();
        return tb - ta;
      });
  }, [selectedDay, reviews]);

  // Build 8 weeks × 7 days grid. Align so each row starts on Sunday.
  const weeks = useMemo(() => {
    if (days.length === 0) return [] as (DayBucket | null)[][];
    const first = new Date(days[0].dateISO + "T00:00:00");
    const dayOfWeek = first.getDay(); // 0=Sun
    const padded: (DayBucket | null)[] = Array(dayOfWeek).fill(null).concat(days);
    while (padded.length % 7 !== 0) padded.push(null);
    const rows: (DayBucket | null)[][] = [];
    for (let i = 0; i < padded.length; i += 7) rows.push(padded.slice(i, i + 7));
    return rows;
  }, [days]);

  const fmtRowLabel = (row: (DayBucket | null)[]) => {
    const first = row.find((d): d is DayBucket => !!d);
    if (!first) return "";
    const d = new Date(first.dateISO + "T00:00:00");
    return d.toLocaleString(undefined, { month: "short", day: "numeric" }).replace(",", "");
  };

  const fmtTooltipDate = (iso: string) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  };

  const hasAny = days.some((d) => d.total > 0);

  return (
    <Card className="p-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold">Sentiment by day</h2>
        <p className="text-sm text-muted-foreground">Daily sentiment pattern across your reviews</p>
      </div>

      {!hasAny ? (
        <SectionEmpty />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "56px repeat(7, 1fr)", gap: "4px" }}>
            <div />
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <div
                key={d}
                style={{
                  fontSize: "11px",
                  color: "#94a3b8",
                  textAlign: "center",
                  paddingBottom: "6px",
                  fontWeight: 500,
                }}
              >
                {d}
              </div>
            ))}
            {weeks.map((row, i) => (
              <Fragment key={`week-${i}`}>
                <div
                  key={`label-${i}`}
                  style={{
                    fontSize: "11px",
                    color: "#94a3b8",
                    textAlign: "right",
                    paddingRight: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    height: "36px",
                  }}
                >
                  {fmtRowLabel(row)}
                </div>
                {row.map((cell, j) => {
                  const cellKey = `${i}-${j}`;
                  if (!cell) {
                    return (
                      <div
                        key={cellKey}
                        style={{
                          width: "100%",
                          height: "36px",
                          borderRadius: "6px",
                          backgroundColor: "#f1f5f9",
                          border: "1px solid #e2e8f0",
                        }}
                      />
                    );
                  }
                  const isEmpty = cell.total === 0;
                  const bg = colorForCell(cell);
                  const isHovered = hoveredCell === cellKey && !isEmpty;
                  const negPct = cell.total ? Math.round((cell.negative / cell.total) * 100) : 0;
                  return (
                    <div key={cellKey} style={{ position: "relative" }}>
                      <div
                        onMouseEnter={() => setHoveredCell(cellKey)}
                        onMouseLeave={() => setHoveredCell((c) => (c === cellKey ? null : c))}
                        onClick={() => { if (!isEmpty) setSelectedDay(cell.dateISO); }}
                        style={{
                          width: "100%",
                          height: "36px",
                          borderRadius: "6px",
                          cursor: isEmpty ? "default" : "pointer",
                          backgroundColor: bg,
                          border: isEmpty ? "1px solid #e2e8f0" : undefined,
                        }}
                      />
                      {isHovered && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "calc(100% + 6px)",
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "#1e293b",
                            color: "white",
                            fontSize: "11px",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            zIndex: 50,
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                          }}
                        >
                          <div>{fmtTooltipDate(cell.dateISO)}</div>
                          <div>{cell.total} review{cell.total === 1 ? "" : "s"}</div>
                          <div>
                            <span style={{ color: "#15803d" }}>{cell.positivePct}% positive</span>
                            {" · "}
                            <span style={{ color: "#991b1b" }}>{negPct}% negative</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", fontSize: "11px", color: "#64748b", flexWrap: "wrap" }}>
            <span>Negative</span>
            {["#fca5a5", "#fcd34d", "#86efac", "#16a34a"].map((c) => (
              <span key={c} style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "3px", backgroundColor: c }} />
            ))}
            <span>Positive</span>
            <span style={{ margin: "0 4px", color: "#cbd5e1" }}>·</span>
            <span style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "3px", backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0" }} />
            <span>No data</span>
          </div>
        </>
      )}

      <Dialog open={!!selectedDay} onOpenChange={(o) => { if (!o) setSelectedDay(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedDay ? fmtTooltipDate(selectedDay) : ""} · {dayReviews.length} review{dayReviews.length === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            {dayReviews.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">No reviews on this day.</div>
            ) : (
              <div className="space-y-3">
                {dayReviews.map((r) => (
                  <div key={r.id} className="border rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{r.author_name || "Anonymous"}</span>
                        {r.platform && <Badge variant="outline" className="text-xs">{r.platform}</Badge>}
                      </div>
                      {typeof r.rating === "number" && (
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star key={s} className={`h-3.5 w-3.5 ${s <= (r.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-sm leading-relaxed text-foreground/90">{r.comment_text || <span className="italic text-muted-foreground">No text</span>}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </Card>
  );
});
SentimentHeatmapSection.displayName = "SentimentHeatmapSection";

export default AnalyticsTab;
